import { useEffect, useRef, useState } from 'react';

const MIN_CENTERED_SEARCH_WIDTH = 150;

/**
 * Below `lg`, the width of the wider of the header's two side groups, so both side columns
 * can take it and the search sits at the header's center. `null` from `lg` up, where the
 * layout is unchanged, and when centering would leave the search narrower than 150px.
 */
export function useCompactSideWidth(isCompact: boolean) {
  const containerRef = useRef<HTMLDivElement>(null);
  const startRef = useRef<HTMLDivElement>(null);
  const endRef = useRef<HTMLDivElement>(null);
  const [sideWidth, setSideWidth] = useState<number | null>(null);

  // Measures the header and its side groups with a ResizeObserver while compact.
  useEffect(() => {
    const container = containerRef.current;
    const start = startRef.current;
    const end = endRef.current;
    if (!isCompact || container == null || start == null || end == null) {
      return;
    }

    const observer = new ResizeObserver(() => {
      const containerStyle = getComputedStyle(container);
      const available =
        container.clientWidth -
        Number.parseFloat(containerStyle.paddingLeft) -
        Number.parseFloat(containerStyle.paddingRight) -
        Number.parseFloat(containerStyle.columnGap) * 2;
      const widest = Math.max(start.getBoundingClientRect().width, end.getBoundingClientRect().width);
      setSideWidth(available - widest * 2 >= MIN_CENTERED_SEARCH_WIDTH ? widest : null);
    });
    observer.observe(container);
    observer.observe(start);
    observer.observe(end);
    return () => observer.disconnect();
  }, [isCompact]);

  return { containerRef, startRef, endRef, sideWidth: isCompact ? sideWidth : null };
}
